<template>
  <div>
    <UAccordion
      v-model="panel"
      type="single"
      :items="accordionItems"
      :ui="{ label: 'font-bold', item: 'last:border-b' }"
    >
      <template #body="{ item }">
        <!-- P16: settings bodies sit on the plain panel surface. -->
        <div
          v-if="item.value === 'nested-1'"
          class="px-1"
        >
          <div class="flex items-center justify-between py-2">
            <span>{{ t('kds.drawer.row_number') }}</span>
            <div class="flex items-center">
              <UButton
                variant="outline"
                color="neutral"
                icon="i-lucide-minus"
                :disabled="itemsPerPage[0] <= 1"
                @click="itemsPerPage[0]--"
              />
              <span class="text-center" style="width: 40px">
                {{ itemsPerPage[0] }}
              </span>
              <UButton
                variant="outline"
                color="neutral"
                icon="i-lucide-plus"
                :disabled="itemsPerPage[0] >= 8"
                @click="itemsPerPage[0]++"
              />
            </div>
          </div>
          <div class="flex items-center justify-between py-2">
            <span>{{ t('kds.drawer.column_number') }}</span>
            <div class="flex items-center">
              <UButton
                variant="outline"
                color="neutral"
                icon="i-lucide-minus"
                :disabled="itemsPerPage[1] <= 1"
                @click="itemsPerPage[1]--"
              />
              <span class="text-center" style="width: 40px">
                {{ itemsPerPage[1] }}
              </span>
              <UButton
                variant="outline"
                color="neutral"
                icon="i-lucide-plus"
                :disabled="itemsPerPage[1] >= 8"
                @click="itemsPerPage[1]++"
              />
            </div>
          </div>
        </div>

        <div
          v-else-if="item.value === 'nested-2'"
          class="px-1 py-2"
        >
          <!-- P16: full-width rows, radio on the RIGHT. -->
          <URadioGroup
            :model-value="selectedFont ?? undefined"
            :items="fontItems"
            indicator="end"
            :ui="{ item: 'w-full py-1.5', label: 'flex-1' }"
            @update:model-value="setFontSize"
          />
        </div>

        <div
          v-else
          class="px-1"
        >
          <button
            v-for="timer in timers"
            :key="timer.title"
            type="button"
            class="w-full flex items-center py-2.5 text-left hover:bg-elevated"
            @click="changeTimer(timer)"
          >
            <UIcon
              class="mr-2 size-4 shrink-0"
              :class="timer.color === 'error' ? 'text-error' : 'text-warning'"
              name="i-lucide-square"
            />

            <span class="flex-1">{{ timer.title }}</span>

            <span>{{ timer.value }} min</span>
          </button>
        </div>
      </template>
    </UAccordion>

    <!-- Theme switch as a plain settings row (owner 2026-07-10) — KDS cards
         follow the theme since the P15/P16 pass, and KDS terminals cannot
         reach the Options screen. Geometry mirrors the UAccordion trigger
         (gap-1.5 / py-3.5 / 20px inherited-color leading icon) so the row
         reads as a sibling of the accordion sections. -->
    <div class="flex w-full items-center gap-1.5 py-3.5">
      <!-- Icon inherits the row's text color, like the accordion trigger icons. -->
      <UIcon :name="store.isDark ? 'i-lucide-moon' : 'i-lucide-sun'" class="size-5 shrink-0" />
      <span class="flex-1 truncate font-bold">{{ t('general.dark_theme') }}</span>
      <USwitch
        :model-value="store.isDark"
        @update:model-value="(v: boolean | 'indeterminate') => Store.set('darkTheme', v === true)"
      />
    </div>

    <!-- Small picker — centered dialog on every breakpoint, no fullscreen
         takeover on mobile (owner 2026-07-10; R23's CodConfirm precedent). -->
    <CodSheet
      v-model="timerDialog"
      show-header
      width="450px"
      :fullscreen-mobile="false"
    >
      <template #title>
        <span :class="timerColor === 'error' ? 'text-error' : 'text-warning'">{{ modelTitle }}</span>
      </template>

      <div class="mt-2 p-4">
        <div class="hide-scrollbar mt-2 flex gap-1 overflow-x-auto">
          <UButton
            v-for="n in 30"
            :key="n"
            class="min-w-12 shrink-0"
            :color="model === n - 1 ? (timerColor === 'error' ? 'error' : 'warning') : 'neutral'"
            :variant="model === n - 1 ? 'solid' : 'outline'"
            :label="String(n)"
            @click="model = n - 1"
          />
        </div>
      </div>

      <div class="flex justify-center">{{ model + 1 }} {{ t('general.minutes_short') }}</div>

      <template #footer>
        <UButton size="lg"
          color="primary"
          variant="solid"
          icon="i-lucide-check"
          @click="setTimer"
        >
          {{ t('general.save') }}
        </UButton>
      </template>
    </CodSheet>
  </div>
</template>

<script setup lang="ts">

import { ref, computed, watch, onMounted, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { IAppStorage, Store, diContainer, IKDSFontSize, AppStorageKey } from 'app-core';
import { useAppStore } from '@/stores/useAppStore';
import { useDisplay } from '@/composables/useDisplay';
import { CodSheet } from 'app-components';

const store = useAppStore()
const appStorage = diContainer.resolve<IAppStorage>('appStorage')
const { t } = useI18n()
const { mobile } = useDisplay()

const props = defineProps({
  limited: {
    type: Boolean,
    default: true,
  }
})

const itemsPerPage = ref([3, 6]);
const fontSize: Ref<IKDSFontSize> = ref('medium')
const yellowTimer = ref(5);
const redTimer = ref(7);
const filterForUser = ref(false);
const timerDialog = ref(false);
const model = ref(4);
const modelTitle = ref(null);
const timerColor = ref('warning');
const panel = ref('nested-1');
const selectedFont: Ref<number | null> = ref(null);

const accordionItems = computed(() => [
  { value: 'nested-1', label: t('kds.drawer.tickets_per_page'), icon: 'i-lucide-layout-grid' },
  { value: 'nested-2', label: t('kds.drawer.font_size'), icon: 'i-lucide-type' },
  ...(!props.limited ? [{ value: 'nested-3', label: t('kds.drawer.timers'), icon: 'i-lucide-clock' }] : []),
])

const fontSizes = computed(() => [
  { id: 1, title: t('kds.drawer.small'), value: mobile.value ? 'm-small' : 'small' },
  { id: 2, title: t('kds.drawer.medium'), value: mobile.value ? 'm-medium' : 'medium' },
  { id: 3, title: t('kds.drawer.large'), value: mobile.value ? 'm-large' : 'large' },
])

const fontItems = computed(() => fontSizes.value.map((font) => ({ label: font.title, value: font.id })))

const timers = computed(() => [
  { title: t('kds.drawer.yellow_timer'), value: yellowTimer.value, color: 'warning' },
  { title: t('kds.drawer.red_timer'), value: redTimer.value, color: 'error' },
]);

const setFontSize = (value: any) => {
  fontSize.value = fontSizes.value.find((grid) => grid.id === value)?.value as IKDSFontSize;
  selectedFont.value = value;
  changeFontSize();
};

const changeTimer = (event: any) => {
  modelTitle.value = event.title;
  model.value = event.value - 1;
  timerDialog.value = true;
  timerColor.value = event.color;
};

watch(itemsPerPage, (value) => {
  setKdsSettings();
}, { deep: true })

const changeFontSize = () => {
  setKdsSettings();
};

const setTimer = () => {
  if (modelTitle.value === t('kds.drawer.yellow_timer')) {
    yellowTimer.value = model.value + 1;

    setKdsSettings();
  } else {
    redTimer.value = model.value + 1;

    setKdsSettings();
  }

  timerDialog.value = false;
};

const setKdsSettings = () => {
  const kdsSettings = {
    itemsPerPage: itemsPerPage.value,
    fontSize: fontSize.value,
    yellowTimer: yellowTimer.value,
    redTimer: redTimer.value,
    filterForUser: filterForUser.value,
  };

  appStorage.local().setItem(AppStorageKey.kdsSettings, kdsSettings);

  Store.set('kdsSettings', kdsSettings);
};

onMounted(() => {
  itemsPerPage.value = store.kdsSettings.itemsPerPage;
  fontSize.value = store.kdsSettings.fontSize;

  if (mobile.value) {
    fontSize.value = fontSizes.value[2].value as IKDSFontSize;
  }

  selectedFont.value = fontSizes.value.find((font) => font.value === fontSize.value)?.id as number;
  yellowTimer.value = store.kdsSettings.yellowTimer;
  redTimer.value = store.kdsSettings.redTimer;
  filterForUser.value = store.kdsSettings.filterForUser;
});
</script>
